import type { CollectDataTemplateActionDto } from 'src/interfaces/collectDataTemplateUpdate'
import {
  CollectDataTemplateStepEvents,
  CollectDataTemplateStepsConfigEvents,
} from 'src/interfaces/collectDataTemplateUpdate'
import type { CollectDataTemplateCreateStepActionDto } from 'src/interfaces/collectDataTemplateUpdate'
import { insurancePolicyIdForApiIfPresent } from 'src/utils/collectDataTemplateUpdateActions'
import { isPersistedConfig, isPersistedStep } from './ids'
import { draftStepToApiRow } from './rowBuilder'
import { diffConfigUpdatePayload, diffStepUpdatePayload, rowToCreatePayload } from './diffPayloads'
import type { FlowEditorConfigDraft, FlowEditorTemplateDocument } from './types'

function indexStepsByServerId(document: FlowEditorTemplateDocument): Map<string, string> {
  const map = new Map<string, string>()

  for (const step of Object.values(document.stepsByLocalId)) {
    if (step.serverId) {
      map.set(step.serverId, step.localId)
    }
  }

  return map
}

function indexConfigsByServerId(document: FlowEditorTemplateDocument): Map<string, string> {
  const map = new Map<string, string>()

  for (const config of Object.values(document.configsByLocalId)) {
    if (config.serverId) {
      map.set(config.serverId, config.localId)
    }
  }

  return map
}

function configCreatePayload(
  config: FlowEditorConfigDraft,
  stepActions: CollectDataTemplateCreateStepActionDto[],
): import('src/interfaces/collectDataTemplateUpdate').CollectDataTemplateCreateConfigPayloadDto {
  const insurancePolicyId = insurancePolicyIdForApiIfPresent(config.insurancePolicyId)
  const payload: import('src/interfaces/collectDataTemplateUpdate').CollectDataTemplateCreateConfigPayloadDto =
    {
      label: config.label,
      delayed: config.delayed,
      order: config.order,
      stepActions,
    }

  const withPolicy = insurancePolicyId !== undefined ? { ...payload, insurancePolicyId } : payload

  if (typeof config.title === 'string' && config.title.trim() !== '') {
    return { ...withPolicy, title: config.title.trim() }
  }

  return withPolicy
}

/**
 * Diff baseline vs draft documents → minimal `CollectDataTemplateUpdateDto.actions`.
 * Uses `serverId` on updates/deletes; omits ids on creates.
 */
export function buildActionsFromDocumentDiff(
  baseline: FlowEditorTemplateDocument,
  draft: FlowEditorTemplateDocument,
): CollectDataTemplateActionDto[] {
  const actions: CollectDataTemplateActionDto[] = []
  const baselineConfigServerIds = indexConfigsByServerId(baseline)
  const baselineStepServerIds = indexStepsByServerId(baseline)
  const draftConfigLocalIds = new Set(draft.configLocalIds)

  for (const configLocalId of draft.configLocalIds) {
    const draftConfig = draft.configsByLocalId[configLocalId]

    if (!draftConfig || draftConfig.stepLocalIds.length === 0) {
      continue
    }

    if (!isPersistedConfig(draftConfig)) {
      const createStepActions: CollectDataTemplateCreateStepActionDto[] = []

      for (const stepLocalId of draftConfig.stepLocalIds) {
        const step = draft.stepsByLocalId[stepLocalId]

        if (!step) {
          continue
        }

        createStepActions.push({
          type: CollectDataTemplateStepEvents.Created,
          payload: rowToCreatePayload(draftStepToApiRow(step, draftConfig, draft.templateId)),
        })
      }

      if (createStepActions.length > 0) {
        actions.push({
          type: CollectDataTemplateStepsConfigEvents.Created,
          payload: configCreatePayload(draftConfig, createStepActions),
        })
      }

      continue
    }

    const baselineConfigLocalId = draftConfig.serverId
      ? baselineConfigServerIds.get(draftConfig.serverId)
      : undefined
    const baselineConfig =
      baselineConfigLocalId !== undefined
        ? baseline.configsByLocalId[baselineConfigLocalId]
        : undefined

    const stepActions: import('src/interfaces/collectDataTemplateUpdate').CollectDataTemplateStepActionDto[] =
      []

    for (const stepLocalId of draftConfig.stepLocalIds) {
      const draftStep = draft.stepsByLocalId[stepLocalId]

      if (!draftStep) {
        continue
      }

      if (!isPersistedStep(draftStep)) {
        stepActions.push({
          type: CollectDataTemplateStepEvents.Created,
          payload: rowToCreatePayload(draftStepToApiRow(draftStep, draftConfig, draft.templateId)),
        })

        continue
      }

      const baselineStepLocalId = baselineStepServerIds.get(draftStep.serverId!)
      const baselineStep =
        baselineStepLocalId !== undefined ? baseline.stepsByLocalId[baselineStepLocalId] : undefined

      if (!baselineStep || baselineStep.configLocalId !== baselineConfigLocalId) {
        stepActions.push({
          type: CollectDataTemplateStepEvents.Created,
          payload: rowToCreatePayload(draftStepToApiRow(draftStep, draftConfig, draft.templateId)),
        })

        continue
      }

      const baselineConfigForRow =
        baseline.configsByLocalId[baselineStep.configLocalId] ?? baselineConfig!

      const prevRow = draftStepToApiRow(baselineStep, baselineConfigForRow, baseline.templateId)
      const nextRow = draftStepToApiRow(draftStep, draftConfig, draft.templateId)
      const patch = diffStepUpdatePayload(prevRow, nextRow, draftStep.serverId!)

      if (patch) {
        stepActions.push({
          type: CollectDataTemplateStepEvents.Updated,
          payload: patch,
        })
      }
    }

    if (baselineConfig) {
      for (const stepLocalId of baselineConfig.stepLocalIds) {
        const baselineStep = baseline.stepsByLocalId[stepLocalId]

        if (!baselineStep?.serverId) {
          continue
        }

        const stillInDraft = draftConfig.stepLocalIds.some((id) => {
          const s = draft.stepsByLocalId[id]

          return s?.serverId === baselineStep.serverId
        })

        if (!stillInDraft) {
          stepActions.push({
            type: CollectDataTemplateStepEvents.Deleted,
            payload: { id: baselineStep.serverId },
          })
        }
      }
    }

    if (!baselineConfig) {
      continue
    }

    const configPatch = diffConfigUpdatePayload(
      {
        label: baselineConfig.label,
        delayed: baselineConfig.delayed,
        order: baselineConfig.order,
        title: baselineConfig.title,
        description: baselineConfig.description,
      },
      {
        label: draftConfig.label,
        delayed: draftConfig.delayed,
        order: draftConfig.order,
        title: draftConfig.title,
        description: draftConfig.description,
      },
      draftConfig.serverId!,
    )

    if (configPatch || stepActions.length > 0) {
      actions.push({
        type: CollectDataTemplateStepsConfigEvents.Updated,
        payload: {
          id: draftConfig.serverId!,
          ...configPatch,
          stepActions: stepActions.length > 0 ? stepActions : undefined,
        },
      })
    }
  }

  for (const configLocalId of baseline.configLocalIds) {
    const baselineConfig = baseline.configsByLocalId[configLocalId]

    if (!baselineConfig?.serverId) {
      continue
    }

    const stillInDraft = Array.from(draftConfigLocalIds).some((id) => {
      const c = draft.configsByLocalId[id]

      return c?.serverId === baselineConfig.serverId
    })

    if (!stillInDraft) {
      actions.push({
        type: CollectDataTemplateStepsConfigEvents.Deleted,
        payload: { id: baselineConfig.serverId },
      })
    }
  }

  return actions
}
